// WbCompleteTicketModal — Phase C10 Group 4.
//
// Captures payment_method + paid_amount + optional notes for
// POST /wb/tickets/:enc_id/complete. On success the response carries:
//   • `ticket`                — the completed TicketDetailDto
//   • `barrier_actions[]`     — queued for the on-prem agent (we just
//                               console.log them in C10 — the WebSocket
//                               protocol that dispatches them is C11+).
//   • `credit_limit_warning`  — when true, surface an extra warning toast.
//
// 422 responses keep the modal OPEN with an inline error so the operator
// can correct the paid amount (the most likely 422 cause) without losing
// what they typed. Other errors also keep the modal open; the inline
// error renders any extracted message.

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { Button } from '../../../../components/ui/Button/Button';
import { useToastStore } from '../../../../store/toastStore';
import { extractErrorMessage } from '../../../../utils/errorMessage';
import { useWbTicket } from '../../context/WbTicketContext';
import type { TicketDetailDto, PaymentMethod } from '../../hooks/useWbTicket';
import styles from './WbTicketRightRail.module.css';

export interface WbCompleteTicketModalProps {
  open: boolean;
  onClose: () => void;
  ticket: TicketDetailDto;
}

const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Cash',
  card: 'Card',
  account: 'Account',
  mixed: 'Mixed',
};

/**
 * Default paid amount based on payment method:
 *   • cash / card  → full ticket total (operator collected it now).
 *   • account / mixed → 0 (caller will fill in the partial later).
 */
function defaultPaidAmount(
  method: PaymentMethod,
  total: number,
): string {
  if (method === 'cash' || method === 'card') {
    return total.toFixed(2);
  }
  return '0';
}

interface AxiosLikeError {
  response?: { status?: number };
}

function isAxiosStatus(err: unknown, status: number): boolean {
  if (err === null || typeof err !== 'object') return false;
  const e = err as AxiosLikeError;
  return e.response?.status === status;
}

export function WbCompleteTicketModal({
  open,
  onClose,
  ticket,
}: WbCompleteTicketModalProps): JSX.Element {
  const { completeTicket } = useWbTicket();
  const pushToast = useToastStore((s) => s.push);
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [paidAmount, setPaidAmount] = useState<string>(() =>
    defaultPaidAmount('cash', ticket.total),
  );
  const [notes, setNotes] = useState<string>('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // When the modal re-opens we want a fresh paid-amount default based on
  // the latest ticket total (the operator may have edited lines between
  // closes). We reset on the open transition.
  useEffect(() => {
    if (open) {
      setPaymentMethod('cash');
      setPaidAmount(defaultPaidAmount('cash', ticket.total));
      setNotes('');
      setInlineError(null);
      setSubmitting(false);
    }
  }, [open, ticket.total]);

  function handlePaymentMethodChange(next: PaymentMethod): void {
    setPaymentMethod(next);
    // Reset paid_amount per the method's default rule. This intentionally
    // overwrites any value the operator typed — the brief says "RESET"
    // unconditionally when the method changes.
    setPaidAmount(defaultPaidAmount(next, ticket.total));
  }

  async function handleSubmit(e?: FormEvent): Promise<void> {
    if (e !== undefined) e.preventDefault();
    if (submitting) return;
    setInlineError(null);

    const parsed = Number(paidAmount);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setInlineError('Paid amount must be zero or greater.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await completeTicket({
        payment_method: paymentMethod,
        paid_amount: parsed,
        notes: notes === '' ? undefined : notes,
      });

      // Barrier actions: agent-protocol dispatch lands in C11+. For C10 we
      // log the queued list so QA can inspect it; the toast tells the
      // operator how many fired.
      // eslint-disable-next-line no-console
      console.log('Barrier actions:', response.barrier_actions);

      pushToast({
        type: 'success',
        message: `Ticket completed. ${response.barrier_actions.length} barrier action(s) queued.`,
      });

      if (response.credit_limit_warning === true) {
        pushToast({
          type: 'warning',
          message: 'Credit limit warning for this customer.',
        });
      }

      onClose();
      // Navigate to the completed ticket's detail page. The server may
      // return a new enc_id in the future (slug rotation); always trust
      // response.ticket.enc_id over the cached one.
      navigate(`/weighbridge/tickets/${response.ticket.enc_id}`);
    } catch (err) {
      // 422 = validation failure (e.g. paid_amount > total, credit-limit
      // block). Other statuses also surface inline so the operator can
      // see what went wrong without losing typed values.
      if (isAxiosStatus(err, 422)) {
        setInlineError(
          extractErrorMessage(err) ?? 'Validation failed — check the values.',
        );
      } else {
        setInlineError(
          extractErrorMessage(err) ?? 'Failed to complete ticket.',
        );
      }
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (submitting) return;
        onClose();
      }}
      title="Complete ticket"
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
            data-testid="wb-complete-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit();
            }}
            loading={submitting}
            data-testid="wb-complete-modal-submit"
          >
            Complete
          </Button>
        </>
      }
    >
      <form
        className={styles.modalBody}
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        data-testid="wb-complete-modal-form"
      >
        {inlineError !== null ? (
          <div className={styles.inlineError} data-testid="wb-complete-modal-error">
            {inlineError}
          </div>
        ) : null}

        <div className={styles.field}>
          <label
            className={styles.fieldLabel}
            htmlFor="wb-complete-payment-method"
          >
            Payment method
          </label>
          <select
            id="wb-complete-payment-method"
            className={styles.select}
            value={paymentMethod}
            onChange={(e) =>
              handlePaymentMethodChange(e.target.value as PaymentMethod)
            }
            disabled={submitting}
            data-testid="wb-complete-payment-method"
            required
          >
            {(['cash', 'card', 'account', 'mixed'] as const).map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABEL[m]}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label
            className={styles.fieldLabel}
            htmlFor="wb-complete-paid-amount"
          >
            Paid amount
          </label>
          <input
            id="wb-complete-paid-amount"
            type="number"
            min={0}
            step={0.01}
            className={styles.input}
            value={paidAmount}
            onChange={(e) => setPaidAmount(e.target.value)}
            disabled={submitting}
            data-testid="wb-complete-paid-amount"
          />
          <span className={styles.fieldHelp}>
            Enter 0 for account customers.
          </span>
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="wb-complete-notes">
            Notes (optional)
          </label>
          <textarea
            id="wb-complete-notes"
            className={styles.textarea}
            value={notes}
            maxLength={5000}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
            placeholder="Customer-facing completion notes…"
            data-testid="wb-complete-notes"
          />
        </div>
      </form>
    </Modal>
  );
}
